/* /pages/MitrasHub.jsx
 *
 * Customer-facing entry point for the Mitra ecosystem.
 * Renders the five Mitra bucket cards in a responsive grid and
 * a "Become a Mitra" CTA. Each card links to /mitras/:bucket which
 * shows filtered listings.
 *
 * Bharat-friendly tone: warm, trustworthy, family-oriented.
 * Mobile-first single column → 2-up tablet → 3-up desktop.
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, ShieldCheck, MapPin, ChevronLeft } from "lucide-react";

import BrandLogo from "@/components/BrandLogo";
import LegalDisclaimer from "@/components/LegalDisclaimer";
import { PUBLIC_MITRA_BUCKETS } from "@/mitras/mitraCategories";
import { useMitraSeo } from "@/mitras/useMitraSeo";
import { FINANCE_MITRA_ENABLED } from "@/lib/featureFlags";

const MitrasHub = () => {
  useMitraSeo({
    title: "Find Your Trusted Mitras · Mera Naksha — Bharat-first home ecosystem",
    description:
      "Connect with verified Design, Construction, Property and Material Mitras across India. Trusted partners for every stage of your home journey.",
    path: "/mitras",
    breadcrumbs: [
      { label: "Home", href: "/" },
      { label: "Mitras", href: "/mitras" },
    ],
  });

  useEffect(() => {
    // Title fallback for the (rare) case JS executes before the hook
    document.title = "Find Your Trusted Mitras · Mera Naksha";
  }, []);

  return (
    <div className="min-h-screen bg-sand-alt flex flex-col" data-testid="mitras-hub-root">
      <header className="bg-navy text-white border-b-4 border-gold">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" data-testid="mitras-hub-brand" className="inline-flex items-center bg-white rounded-lg px-2 py-1 shadow-sm">
            <BrandLogo size="sm" asLink={false} withTagline lang="en" />
          </Link>
          <Link
            to="/"
            data-testid="mitras-hub-back-home"
            className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-white/80 hover:text-gold"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative overflow-hidden bg-navy text-white"
        data-testid="mitras-hub-hero"
      >
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
        <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-gold/5 blur-3xl pointer-events-none" />
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12 sm:py-16 lg:py-20 relative">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-gold text-[11px] font-extrabold tracking-[0.18em] uppercase">
            <Users className="h-3.5 w-3.5" />
            Mera Naksha — Mitra Ecosystem
          </div>
          <h1
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight"
            data-testid="mitras-hub-title"
          >
            Find Your <span className="text-gold">Trusted Mitras</span>
          </h1>
          <p
            className="mt-4 text-[15px] sm:text-base text-white/80 max-w-2xl leading-relaxed"
            data-testid="mitras-hub-subtitle"
          >
            Connect with experienced professionals and trusted service partners
            for every stage of your home journey — from design and construction
            to property{FINANCE_MITRA_ENABLED ? ", materials and finance" : " and materials"}.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2.5 text-[12px] text-white/70">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-gold" />
              Verified by Team Mera Naksha
            </span>
            <span className="opacity-40">·</span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-gold" />
              Pan-India coverage
            </span>
          </div>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section
        className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14"
        data-testid="mitras-hub-categories"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {PUBLIC_MITRA_BUCKETS.map((bucket) => (
            <MitraBucketCard key={bucket.key} bucket={bucket} />
          ))}

          {/* "Become a Mitra" recruitment card to round off the grid */}
          <Link
            to="/professional/register"
            data-testid="mitras-hub-become-cta"
            className="group rounded-2xl bg-navy text-white border-2 border-gold overflow-hidden shadow-[0_24px_48px_-24px_rgba(11,37,69,0.45)] hover:shadow-[0_28px_56px_-20px_rgba(11,37,69,0.55)] transition-shadow"
          >
            <div className="h-full p-5 sm:p-6 flex flex-col gap-3">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold text-navy text-[10.5px] font-extrabold uppercase tracking-wider w-fit">
                Join the network
              </div>
              <div className="text-2xl font-extrabold leading-tight">
                Become a <span className="text-gold">Mitra</span>
              </div>
              <p className="text-[13px] text-white/75 leading-relaxed">
                Are you an architect, contractor{FINANCE_MITRA_ENABLED ? ", supplier or finance expert" : " or supplier"}?
                Reach families across Bharat building their dream homes.
              </p>
              <div className="mt-auto inline-flex items-center gap-1.5 text-gold font-extrabold text-[14px] group-hover:gap-2.5 transition-all">
                Register Now <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </Link>
        </div>

        {/* Trust strip */}
        <div className="mt-10 sm:mt-12 rounded-2xl bg-white border-2 border-border p-5 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center sm:text-left">
            <TrustPoint
              icon={<ShieldCheck className="h-5 w-5 text-gold-dark" />}
              title="Verified profiles"
              body="Every Mitra is screened by Team Mera Naksha before going live."
            />
            <TrustPoint
              icon={<Users className="h-5 w-5 text-gold-dark" />}
              title="Family-first network"
              body="Built for Bharat — local language, local context, local trust."
            />
            <TrustPoint
              icon={<MapPin className="h-5 w-5 text-gold-dark" />}
              title="Pan-India reach"
              body="From Tier-1 metros to rural districts. Coverage growing every week."
            />
          </div>
        </div>
      </section>

      <LegalDisclaimer />
    </div>
  );
};

// ---------- Sub-components ----------

const MitraBucketCard = ({ bucket }) => {
  const { key, label, labelHi, tagline, Icon, accent, accentSoft } = bucket;
  return (
    <Link
      to={`/mitras/${key}`}
      data-testid={`mitra-bucket-card-${key}`}
      className="group rounded-2xl bg-white border-2 border-border overflow-hidden shadow-[0_18px_38px_-22px_rgba(11,37,69,0.18)] hover:shadow-[0_24px_48px_-18px_rgba(11,37,69,0.28)] hover:-translate-y-0.5 transition-all"
    >
      <div className="p-5 sm:p-6 flex flex-col gap-3 h-full">
        <div
          className="h-12 w-12 rounded-xl grid place-items-center"
          style={{ background: accentSoft, color: accent }}
          aria-hidden
        >
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold" style={{ color: accent }}>
            {labelHi}
          </div>
          <div className="mt-1 text-[20px] font-extrabold text-navy leading-tight">
            {label}
          </div>
        </div>
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          {tagline}
        </p>
        <div className="mt-auto inline-flex items-center gap-1.5 font-extrabold text-[13.5px] text-navy group-hover:gap-2.5 transition-all">
          Explore Mitras <ArrowRight className="h-4 w-4" />
        </div>
      </div>
      <div className="h-1.5" style={{ background: accent }} />
    </Link>
  );
};

const TrustPoint = ({ icon, title, body }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-2.5">
    <div className="h-9 w-9 rounded-lg bg-gold/10 grid place-items-center mx-auto sm:mx-0 shrink-0">
      {icon}
    </div>
    <div>
      <div className="font-extrabold text-navy text-[13.5px]">{title}</div>
      <div className="text-[12.5px] text-muted-foreground leading-relaxed mt-0.5">
        {body}
      </div>
    </div>
  </div>
);

export default MitrasHub;
